import React from "react";
import Input from "../../../../../components/FormComponents/Input";
import GlobalFieldSelection from "../../../EditFieldAttribute/GlobalFieldSelection";
import useFetchData from "../../../../../hooks/useFetchData";
import { getLabelForKey } from "../../../../../utils/Config/DefaultConfig/formFieldConfig";
import { MasterdataLookupMapping } from "./EditMasterDataLookup";
import { OcrFieldMapping } from "../../Basic/EditOcr";
import { DocumentOcrOutputMapping } from "../../Basic/EditDocumentOcr";

const EditGlobalData = (props) => {
  const { editFieldData, setEditFieldData, tableID, fields } = props;
  const handleChangeInput = (name, value) => {
    setEditFieldData({ ...editFieldData, [name]: value });
  };
  const handleChangeValidationInput = (name, value) => {
    setEditFieldData({ ...editFieldData, validate: { ...editFieldData.validate, [name]: value } });
  };

  const handleChangeGlobalField = (data) => {
    const { components, global_field } = data;
    setEditFieldData({ ...editFieldData, global_field, components });
  };

  return (
    <>
      <Input
        label={getLabelForKey("label")}
        value={editFieldData.label}
        name="label"
        onChange={({ target }) => handleChangeInput(target.name, target.value)}
      />
      <Input
        label={getLabelForKey("form_display_label")}
        value={editFieldData.form_display_label}
        name="form_display_label"
        onChange={({ target }) => handleChangeInput(target.name, target.value)}
      />
      <div className="d_flex">
        <Input
          type="checkbox"
          label={getLabelForKey("required")}
          checked={editFieldData.validate.required}
          name="required"
          onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
          containerClassName="w_50"
        />
        <Input
          type="checkbox"
          label={getLabelForKey("disable")}
          checked={editFieldData.disable}
          name="disable"
          onChange={({ target }) => handleChangeInput(target.name, target.checked)}
          containerClassName="w_50"
        />
      </div>
      <Input
        type="checkbox"
        label={getLabelForKey("backend_validation")}
        checked={editFieldData.validate.backend_validation}
        name="backend_validation"
        size="medium"
        containerClassName="w_50"
        onChange={({ target }) => handleChangeValidationInput(target.name, target.checked)}
      />
      <GlobalFieldSelection value={editFieldData.global_field} onChange={handleChangeGlobalField} tableId={tableID} />
      <GlobalFieldPreview
        tableID={tableID}
        globalFieldID={editFieldData?.global_field?.id}
        fields={fields}
        editFieldData={editFieldData}
        setEditFieldData={setEditFieldData}
      />
      <div className="field_horizontal_container">
        <Input
          label={getLabelForKey("help_text")}
          value={editFieldData.help_text}
          name="help_text"
          onChange={({ target }) => handleChangeInput(target.name, target.value)}
        />
      </div>
    </>
  );
};

export default EditGlobalData;

const GlobalFieldPreview = ({ tableID, globalFieldID, fields, editFieldData, setEditFieldData }) => {
  let { data: instance } = useFetchData(`/state-machine/${tableID}`, [globalFieldID]);
  instance = instance?.config || null;
  const globalField = instance?.global_data_form?.fields?.find((field) => field.id === globalFieldID);

  const renderValue = (value) => {
    if (typeof value === "object" && value !== null) {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (typeof value === "boolean") {
      return value ? "true" : "false";
    }
    return value;
  };

  return (
    <div>
      {globalField && (
        <>
          <div className="sm_blueprint_editBox">
            <label className="builder_label">Global Field Preview</label>

            <table className="preview_table">
              <tbody>
                {globalField.validate ? (
                  Object.entries(globalField.validate)
                    .filter(([key]) => key !== "custom_js" && key !== "required")
                    .map(([key, value]) => (
                      <tr key={key}>
                        <td className="key_col">{getLabelForKey(key)}</td>
                        <td className="value_col">{renderValue(value)}</td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="2" className="no_data">
                      No validation rules found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <GlobalFieldConditionalEditor
            editFieldData={editFieldData}
            setEditFieldData={setEditFieldData}
            globalField={globalField}
            formFields={fields}
          />
        </>
      )}
    </div>
  );
};

const GlobalFieldConditionalEditor = ({ globalField, formFields, editFieldData, setEditFieldData }) => {
  switch (globalField.type) {
    case "masterdata-lookup":
      return (
        <EditGlobalFieldForMasterData
          globalField={globalField}
          formFields={formFields}
          editFieldData={editFieldData}
          setEditFieldData={setEditFieldData}
        />
      );
    case "ocr":
    case "ai-doc-analyzer":
      return (
        <EditGlobalFieldForOcr
          globalField={globalField}
          formFields={formFields}
          editFieldData={editFieldData}
          setEditFieldData={setEditFieldData}
        />
      );
    case "document-ocr":
      return (
        <EditGlobalFieldForDocumentOcr
          globalField={globalField}
          formFields={formFields}
          editFieldData={editFieldData}
          setEditFieldData={setEditFieldData}
        />
      );
    default:
      return null;
  }
};

const EditGlobalFieldForMasterData = ({ globalField, formFields, editFieldData, setEditFieldData }) => {
  const { data, loading, error } = useFetchData("/rdbms");

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !data || !Array.isArray(data)) {
    console.error("Failed to load RDBMS data:", error);
    return <div>Unable to load required data. Please try again later.</div>;
  }

  const selectedRdbms = data.find((rdbms) => rdbms.id === globalField.masterdata_id);

  if (!selectedRdbms) {
    console.warn(`No RDBMS found for masterdata_id: ${globalField.masterdata_id}`);
    return <div>No matching master data found for the selected field.</div>;
  }

  const handleChange = (name, value) => {
    setEditFieldData((prevData) => ({ ...prevData, [name]: value }));
  };

  return (
    <MasterdataLookupMapping
      mapping={editFieldData.rdbms_to_form_map || []}
      formFields={formFields}
      rdbmsFields={selectedRdbms.fields || []}
      onChange={(data) => handleChange("rdbms_to_form_map", data)}
    />
  );
};

const EditGlobalFieldForOcr = ({ formFields, editFieldData, setEditFieldData }) => {
  const handleChange = (name, value) => {
    setEditFieldData((prevData) => ({ ...prevData, [name]: value }));
  };
  return (
    <OcrFieldMapping
      map={editFieldData.field_question_mapping || []}
      onChange={(mapping) => handleChange("field_question_mapping", mapping)}
      fields={formFields}
    />
  );
};

const EditGlobalFieldForDocumentOcr = ({ globalField, formFields, editFieldData, setEditFieldData }) => {
  return (
    <DocumentOcrOutputMapping
      mapping={editFieldData.output_mapping || []}
      onChange={(newMapping) => setEditFieldData({ ...editFieldData, output_mapping: newMapping })}
      documentType={globalField.document_type}
      formFields={formFields}
    />
  );
};
